'use client';

import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
  useState,
} from 'react';

import classnames from 'classnames';

import type { BaseComponentProps } from '../../base-component.types';
import { Dialog, DialogContent, DialogTrigger } from '../../dialog';
import { Icon } from '../../icon';

import styles from './SubMenu.module.scss';

export type SubMenuProps = ComponentPropsWithoutRef<'li'> &
  BaseComponentProps & {
    children?: ReactNode;
    icon?: ReactNode;
    title?: ReactNode;
    defaultOpen?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
    firstchild?: boolean;
    onOpenChange?: (open: boolean) => void;
  };

export function BottomTabSubMenu({
  style,
  children,
  icon,
  className,
  title,
  defaultOpen = false,
  prefix,
  suffix,
  firstchild,
  onOpenChange,
  testId = 'ui-bottom-tab-navbar_submenu',
  ref,
  ...rest
}: SubMenuProps & { ref?: Ref<HTMLLIElement> }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <li
      style={style}
      ref={ref}
      data-testid={testId}
      className={classnames(styles['navbar-submenu'], className)}
      {...rest}
    >
      <Dialog
        open={open}
        onOpenChange={(newOpen) => {
          setOpen(newOpen);
          if (onOpenChange) onOpenChange(newOpen);
        }}
      >
        <DialogTrigger
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            if (onOpenChange) onOpenChange(open);
            setOpen(!open);
          }}
          asChild={true}
          className={styles['inner-item']}
          tabIndex={0}
        >
          <div>
            {icon && <Icon icon={icon} size="sm" />}
            {prefix && (
              <span className={styles['prefix-wrapper']}>{prefix}</span>
            )}
            {title && !firstchild && (
              <span className={styles['content-wrapper']}>{title}</span>
            )}
            {suffix && (
              <span
                className={styles['suffix-wrapper']}
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                  event.stopPropagation();
                }}
              >
                {suffix}
              </span>
            )}
          </div>
        </DialogTrigger>
        <DialogContent className={styles['content']}>
          <div className={styles['content-header']}>
            {icon && <Icon icon={icon} size="sm" />}
            {title && (
              <span
                className={classnames(
                  styles['content-wrapper'],
                  styles['content-wrapper__title'],
                )}
              >
                {title}
              </span>
            )}
          </div>
          <hr className={styles['divider']} />
          <ul
            className={classnames(styles['inner-list-items'])}
            onClick={() => {
              if (onOpenChange) onOpenChange(open);
              setOpen(!open);
            }}
          >
            {children}
          </ul>
        </DialogContent>
      </Dialog>
    </li>
  );
}
